import { useCallback } from 'react';
import { useDataProvider, useGetIdentity } from 'react-admin';
import { EndorsementRateRow } from '../utils/endorsementNormalization';

export interface EndorsementImportResult {
    created: number;
    updated: number;
    skipped: number;
    errors: string[];
}

export function useEndorsementRatesImport(selectedSpreadsheet: string) {
    const user = useGetIdentity();
    const dataProvider = useDataProvider();

    const processBatch = useCallback(
        async (
            batch: EndorsementRateRow[]
        ): Promise<EndorsementImportResult> => {
            const userId = user?.identity?.id;
            const result: EndorsementImportResult = {
                created: 0,
                updated: 0,
                skipped: 0,
                errors: [],
            };

            if (userId == null) {
                console.warn(
                    'useEndorsementRatesImport: No user ID available, proceeding without created_by. Value:',
                    userId
                );
            }

            // Cache endorsement definition lookups to avoid repeated queries
            const defsCache = new Map<string, boolean>();

            const validateEndorsementExists = async (
                endorsementName: string
            ): Promise<boolean> => {
                if (defsCache.has(endorsementName))
                    return defsCache.get(endorsementName)!;
                const { data } = await dataProvider.getList(
                    'endorsement_definitions',
                    {
                        filter: { endorsement: endorsementName },
                        pagination: { page: 1, perPage: 1 },
                        sort: { field: 'id', order: 'ASC' },
                    }
                );
                const exists = data.length > 0;
                defsCache.set(endorsementName, exists);
                return exists;
            };

            console.log(
                `useEndorsementRatesImport: Starting batch of ${batch.length} rows. userId=${userId} (type=${typeof userId})`
            );
            if (batch.length > 0) {
                console.log(
                    'useEndorsementRatesImport: First row sample:',
                    JSON.stringify(batch[0])
                );
            }

            for (const row of batch) {
                try {
                    const record: Record<string, any> = {
                        carrier: row.carrier,
                        crop_year: row.crop_year,
                        state: row.state,
                        county_code: row.county_code,
                        county: row.county,
                        crop: row.crop,
                        form: row.form,
                        endorsement: row.endorsement,
                        rate: row.rate,
                        created_at: new Date().toISOString(),
                        spreadsheet_type: selectedSpreadsheet,
                    };
                    // Note: created_by is a UUID FK to auth.users.
                    // The identity.id from useGetIdentity is a sales.id (number),
                    // not an auth UUID. Skip created_by to avoid type mismatch.

                    try {
                        await dataProvider.create('endorsement_rates', {
                            data: record,
                        });
                        result.created++;
                    } catch (createError: any) {
                        if (
                            createError?.message?.includes(
                                'duplicate key value violates unique constraint'
                            )
                        ) {
                            // Find existing record to update
                            const existing = await dataProvider.getList(
                                'endorsement_rates',
                                {
                                    filter: {
                                        carrier: row.carrier,
                                        crop_year: row.crop_year,
                                        state: row.state,
                                        county: row.county,
                                        crop: row.crop,
                                        form: row.form,
                                        endorsement: row.endorsement,
                                    },
                                    pagination: {
                                        page: 1,
                                        perPage: 1,
                                    },
                                    sort: {
                                        field: 'id',
                                        order: 'ASC',
                                    },
                                }
                            );

                            if (existing.data.length > 0) {
                                await dataProvider.update('endorsement_rates', {
                                    id: existing.data[0].id,
                                    data: {
                                        rate: row.rate,
                                        spreadsheet_type: selectedSpreadsheet,
                                    },
                                    previousData: existing.data[0],
                                });
                                result.updated++;
                            } else {
                                result.skipped++;
                            }
                        } else {
                            result.errors.push(
                                `${row.carrier}/${row.county}/${row.crop}/${row.form}/${row.endorsement}: ${createError?.message}`
                            );
                        }
                    }
                } catch (err: any) {
                    result.errors.push(
                        `${row.carrier}/${row.county}: ${err?.message}`
                    );
                }
            }

            console.log(
                `useEndorsementRatesImport: Created ${result.created}, Updated ${result.updated}, Skipped ${result.skipped}, Errors ${result.errors.length}`
            );
            return result;
        },
        [dataProvider, user?.identity?.id, selectedSpreadsheet]
    );

    return processBatch;
}
